import { useCallback, useEffect, useRef, useState } from "react";
import { coachApi } from "@/api";
import type { ActivitySummary, PlannedWorkout, WorkoutPayload } from "@/lib/types";
import { useUI } from "@/lib/theme";
import { addMonths, monthGrid, monthStart, toDateStr } from "@/lib/calendar";
import { MonthPicker } from "@/components/ui";
import ActivityCard from "@/components/calendar/ActivityCard";

const SPORTS = ["Carrera", "Ciclismo", "Natacion", "Hyrox", "Fuerza", "Movilidad", "Descanso", "Otro"] as const;
const INTENSITIES = ["easy", "moderate", "hard", "rest"] as const;

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted/20 text-muted",
  published: "bg-accent/15 text-accent",
  done: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  skipped: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
};

function statusChip(status: string, t: (k: string) => string) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[status] ?? ""}`}>
      {t(`cal_status_${status}`)}
    </span>
  );
}

function intensityDot(intensity?: string | null) {
  const map: Record<string, string> = {
    easy: "bg-emerald-400",
    moderate: "bg-amber-400",
    hard: "bg-rose-500",
    rest: "bg-muted",
  };
  if (!intensity || !map[intensity]) return null;
  return <span className={`inline-block h-2 w-2 rounded-full ${map[intensity]}`} aria-hidden />;
}

// ---- Modal de crear/editar ----

interface ModalState {
  open: boolean;
  date: string;
  workout: PlannedWorkout | null;
}

const BLANK_PAYLOAD: WorkoutPayload = {
  title: "",
  sport: "Carrera",
  intensity: null,
  duration_min: undefined,
  description: "",
};

function WorkoutModal({
  athleteId,
  state,
  onClose,
  onSaved,
  onDeleted,
}: {
  athleteId: string;
  state: ModalState;
  onClose: () => void;
  onSaved: (w: PlannedWorkout) => void;
  onDeleted: (id: string) => void;
}) {
  const { t } = useUI();
  const [payload, setPayload] = useState<WorkoutPayload>(state.workout?.payload ?? BLANK_PAYLOAD);
  const [publish, setPublish] = useState(state.workout?.status === "published");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEdit = !!state.workout;

  useEffect(() => {
    setPayload(state.workout?.payload ?? BLANK_PAYLOAD);
    setPublish(state.workout?.status === "published");
    setConfirmDelete(false);
  }, [state.workout]);

  const set = (k: keyof WorkoutPayload, v: unknown) =>
    setPayload((p) => ({ ...p, [k]: v }));

  const handleSave = async (forcePublish = publish) => {
    if (!payload.title.trim()) return;
    setBusy(true);
    try {
      const status = forcePublish ? "published" : "draft";
      if (isEdit && state.workout) {
        await coachApi.updateWorkout(athleteId, state.workout.id, {
          scheduledDate: state.date,
          payload,
          status,
        });
        onSaved({ ...state.workout, scheduled_date: state.date, payload, status });
      } else {
        const { workout } = await coachApi.createWorkout(athleteId, {
          scheduledDate: state.date,
          payload,
          status,
        });
        onSaved(workout);
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!state.workout) return;
    setBusy(true);
    try {
      await coachApi.deleteWorkout(athleteId, state.workout.id);
      onDeleted(state.workout.id);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  if (!state.open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div ref={ref} className="relative z-10 w-full max-w-md rounded-2xl border border-line bg-panel p-6 shadow-2xl">
        <h3 className="mb-4 text-base font-bold">
          {isEdit ? t("cal_edit_workout") : t("cal_new_workout")} · {state.date}
        </h3>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted">{t("cal_title_label")} *</label>
            <input
              className="w-full rounded-lg border border-line bg-panel2 px-3 py-2 text-sm outline-none focus:border-accent"
              value={payload.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Ej: Rodaje suave 45'"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted">{t("cal_sport_label")}</label>
              <select
                className="w-full rounded-lg border border-line bg-panel2 px-3 py-2 text-sm outline-none focus:border-accent"
                value={payload.sport ?? "Carrera"}
                onChange={(e) => set("sport", e.target.value)}
              >
                {SPORTS.map((s) => (
                  <option key={s} value={s}>
                    {s === "Hyrox" || s === "Fuerza" || s === "Movilidad" || s === "Descanso" || s === "Otro"
                      ? t(`cal_sport_${s}`)
                      : t(`cat_${s}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted">{t("cal_intensity_label")}</label>
              <select
                className="w-full rounded-lg border border-line bg-panel2 px-3 py-2 text-sm outline-none focus:border-accent"
                value={payload.intensity ?? ""}
                onChange={(e) => set("intensity", e.target.value || null)}
              >
                <option value="">—</option>
                {INTENSITIES.map((i) => (
                  <option key={i} value={i}>{t(`cal_int_${i}`)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-muted">{t("cal_duration_label")}</label>
            <input
              type="number"
              min={0}
              max={600}
              className="w-full rounded-lg border border-line bg-panel2 px-3 py-2 text-sm outline-none focus:border-accent"
              value={payload.duration_min ?? ""}
              onChange={(e) => set("duration_min", e.target.value ? Number(e.target.value) : undefined)}
              placeholder="60"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-muted">{t("cal_description_label")}</label>
            <textarea
              rows={3}
              className="w-full rounded-lg border border-line bg-panel2 px-3 py-2 text-sm outline-none focus:border-accent resize-none"
              value={payload.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Bloques, ritmos, zonas, notas…"
            />
          </div>
        </div>

        {confirmDelete ? (
          <div className="mt-4 rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-sm">
            <p className="mb-2 font-medium text-rose-600 dark:text-rose-400">{t("cal_delete_confirm")}</p>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={busy}
                className="rounded-lg bg-rose-500 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60 hover:bg-rose-600"
              >
                {t("cal_delete")}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-muted hover:text-fg"
              >
                {t("cal_cancel")}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <button
                onClick={() => void handleSave(false)}
                disabled={busy || !payload.title.trim()}
                className="rounded-xl border border-line bg-panel2 px-3 py-2 text-sm font-medium text-muted transition hover:text-fg disabled:opacity-50"
              >
                {t("cal_save")}
              </button>
              <button
                onClick={() => void handleSave(true)}
                disabled={busy || !payload.title.trim()}
                className="rounded-xl border border-accent/40 bg-accent/10 px-3 py-2 text-sm font-semibold text-accent transition hover:bg-accent/20 disabled:opacity-50"
              >
                {t("cal_publish")}
              </button>
            </div>
            <div className="flex gap-2">
              {isEdit && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-500/20 dark:text-rose-400"
                >
                  {t("cal_delete")}
                </button>
              )}
              <button
                onClick={onClose}
                className="rounded-xl border border-line px-3 py-2 text-sm font-medium text-muted transition hover:text-fg"
              >
                {t("cal_cancel")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Componente principal ----

export default function CoachCalendar({ athleteId }: { athleteId: string }) {
  const { t, lang } = useUI();
  const [month, setMonth] = useState(() => monthStart(new Date()));
  const [workouts, setWorkouts] = useState<PlannedWorkout[]>([]);
  const [acts, setActs] = useState<ActivitySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalState>({ open: false, date: "", workout: null });

  const grid = monthGrid(month);
  const from = toDateStr(grid[0]![0]!);
  const to   = toDateStr(grid[grid.length - 1]![6]!);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      coachApi.calendar(athleteId, from, to),
      coachApi.activities(athleteId, from, to).catch(() => ({ activities: [] })),
    ])
      .then(([cal, actsRes]) => {
        setWorkouts(cal.workouts);
        setActs(actsRes.activities);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [athleteId, from, to]);

  useEffect(() => { load(); }, [load]);

  const workoutsByDate = new Map<string, PlannedWorkout[]>();
  for (const w of workouts) {
    const arr = workoutsByDate.get(w.scheduled_date) ?? [];
    arr.push(w);
    workoutsByDate.set(w.scheduled_date, arr);
  }

  const actsByDate = new Map<string, ActivitySummary[]>();
  for (const a of acts) {
    const ds = a.start_local.slice(0, 10);
    const arr = actsByDate.get(ds) ?? [];
    arr.push(a);
    actsByDate.set(ds, arr);
  }

  const DAY_ABBR =
    lang === "es"
      ? ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]
      : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const openCreate = (date: string) => setModal({ open: true, date, workout: null });
  const openEdit   = (date: string, w: PlannedWorkout) => setModal({ open: true, date, workout: w });

  const handleSaved   = (w: PlannedWorkout) => {
    setWorkouts((prev) => {
      const without = prev.filter((x) => x.id !== w.id);
      return [...without, w].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
    });
  };
  const handleDeleted = (id: string) => setWorkouts((prev) => prev.filter((x) => x.id !== id));

  const today     = toDateStr(new Date());
  const thisMonth = month.getMonth();

  return (
    <div>
      {/* Navegación de mes — card unificada */}
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-line bg-panel px-3 py-2 shadow-sm">
        <button
          onClick={() => setMonth((m) => addMonths(m, -1))}
          className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-muted transition hover:bg-panel2 hover:text-fg"
        >
          ‹ {t("cal_month_prev")}
        </button>
        <div className="flex flex-1 justify-center">
          <MonthPicker
            value={month}
            onChange={setMonth}
            todayLabel={t("cal_today")}
            openLabel={t("cal_open_picker")}
            lang={lang}
          />
        </div>
        <button
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-muted transition hover:bg-panel2 hover:text-fg"
        >
          {t("cal_month_next")} ›
        </button>
      </div>

      {/* Cabecera de días */}
      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-muted">
        {DAY_ABBR.map((d) => <div key={d}>{d}</div>)}
      </div>

      {/* Rejilla mensual */}
      <div className="grid grid-cols-7 gap-1">
        {grid.flatMap((week) =>
          week.map((day) => {
            const ds          = toDateStr(day);
            const isToday     = ds === today;
            const isOther     = day.getMonth() !== thisMonth;
            const dayWorkouts = workoutsByDate.get(ds) ?? [];
            const dayActs     = actsByDate.get(ds) ?? [];

            return (
              <div
                key={ds}
                className={`min-h-[104px] rounded-xl border p-2 transition
                  ${isToday
                    ? "border-accent/60 bg-accent/10 ring-1 ring-accent/30"
                    : isOther
                      ? "border-line/30 bg-panel2/15 opacity-50"
                      : "border-line bg-panel/60"}`}
              >
                {/* Cabecera del día */}
                <div className="mb-1.5 flex items-center justify-between">
                  <span className={`text-[11px] font-semibold ${isToday ? "text-accent" : "text-muted"}`}>
                    {day.getDate()}
                  </span>
                  {!isOther && (
                    <button
                      onClick={() => openCreate(ds)}
                      title={t("cal_new_workout")}
                      className="rounded px-0.5 text-sm leading-none text-muted transition hover:text-accent"
                    >
                      +
                    </button>
                  )}
                </div>

                {/* Entrenos del día */}
                <div className="space-y-1">
                  {loading && dayWorkouts.length === 0 && !isOther && (
                    <div className="h-5 animate-pulse rounded bg-panel2" />
                  )}
                  {dayWorkouts.map((w) => (
                    <button
                      key={w.id}
                      onClick={() => openEdit(ds, w)}
                      className="w-full rounded border border-line bg-panel px-1.5 py-1 text-left text-[11px] transition hover:border-accent/40 hover:bg-accent/5"
                    >
                      <div className="flex items-center gap-1 truncate font-medium">
                        {intensityDot(w.payload.intensity)}
                        <span className="truncate">{w.payload.title}</span>
                      </div>
                      <div className="mt-0.5 flex items-center justify-between gap-1">
                        {statusChip(w.status, t)}
                        {w.payload.duration_min && (
                          <span className="text-muted">{w.payload.duration_min}'</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Actividades reales del atleta */}
                <div className="mt-1 space-y-1">
                  {dayActs.map((a) => (
                    <ActivityCard key={a.id} act={a} />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <WorkoutModal
        athleteId={athleteId}
        state={modal}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </div>
  );
}
